import {
  createContext,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
  type MouseEvent,
  type ReactNode,
} from "react";
import { useNative } from "../../../shared/phone/media";
import { Icon } from "../../components/ui/Icon";
import { addLine, pieceText, pieceTexts, setPiece, type At } from "./blocks";

/**
 * Words changed where they are read. Each paragraph, lead-in and list line is a piece: pointing at one shows
 * its pencil, a click opens it in a box in its place, Enter in a list line saves it and opens the next. Under
 * the pieces the words stay plain text with blank lines and "- ", so the writers and the page share one shape.
 */

interface Open {
  at: At;
  /** A new list line at `at.j`, not in the words until it has some. */
  fresh?: boolean;
}

interface Ctx {
  text: string;
  was: Set<string>;
  open: Open | null;
  setOpen: (o: Open | null) => void;
  save: (at: At, value: string, fresh: boolean, next: boolean) => void;
}

const InPlaceCtx = createContext<Ctx | null>(null);

const same = (a: At, b: At) => a.i === b.i && a.j === b.j;
// Why: the box's buttons take the click without taking focus, so leaving the box never saves before Cancel.
const hold = (e: MouseEvent) => e.preventDefault();
const countLines = (v: string) => Math.max(1, v.split(/\n+/).filter((l) => l.trim()).length);

export function InPlace({ text, original, onChange, children }: { text: string; original: string; onChange: (text: string) => void; children: ReactNode }) {
  const [open, setOpen] = useState<Open | null>(null);
  const was = useMemo(() => pieceTexts(original), [original]);
  const own = useRef(text);
  // Why: words changed from outside (Undo my edit, a reload) close a box that was open on the old words.
  useEffect(() => {
    if (text !== own.current) setOpen(null);
    own.current = text;
  }, [text]);
  const save = (at: At, value: string, fresh: boolean, next: boolean) => {
    const out = fresh && typeof at.j === "number" ? addLine(text, at.i, at.j, value) : setPiece(text, at, value);
    own.current = out;
    if (out !== text) onChange(out);
    setOpen(next && typeof at.j === "number" && value.trim() ? { at: { i: at.i, j: at.j + countLines(value) }, fresh: true } : null);
  };
  return <InPlaceCtx.Provider value={{ text, was, open, setOpen, save }}>{children}</InPlaceCtx.Provider>;
}

type Tag = "div" | "p" | "li" | "h3";

/** One piece. Outside InPlace it is the plain element the page always drew. */
export function Piece({ at, as: T = "div", className = "", children }: { at: At; as?: Tag; className?: string; children: ReactNode }) {
  const c = useContext(InPlaceCtx);
  if (!c) return <T className={className || undefined}>{children}</T>;
  if (c.open && !c.open.fresh && same(c.open.at, at)) return <Editor as={T} at={at} initial={pieceText(c.text, at)} />;
  const words = pieceText(c.text, at);
  const go = () => c.setOpen({ at });
  return (
    <T
      className={`${className} ip-b${c.was.has(words) ? "" : " mine"}`}
      tabIndex={0}
      role="button"
      title="Click to change"
      onClick={(e: MouseEvent) => {
        // Why: a link or an info button inside the words keeps doing its own job.
        if (!(e.target as HTMLElement).closest("a, button")) go();
      }}
      onKeyDown={(e: KeyboardEvent) => {
        if (e.target === e.currentTarget && (e.key === "Enter" || e.key === " ")) {
          e.preventDefault();
          go();
        }
      }}
    >
      <span className="ip-pen" aria-hidden="true">
        <Icon name="pen" />
      </span>
      {children}
    </T>
  );
}

/** Where a new list line opens: after line `j - 1`, or anywhere in paragraph `i` for a list drawn in groups. */
export function NewLine({ i, j, as = "div" }: { i: number; j?: number; as?: Tag }) {
  const c = useContext(InPlaceCtx);
  const o = c?.open;
  if (!o?.fresh || o.at.i !== i || (j !== undefined && o.at.j !== j)) return null;
  return <Editor as={as} at={o.at} initial="" fresh />;
}

/** "Add a line" under a list, only while its words can be changed. */
export function AddLine({ i, count }: { i: number; count: number }) {
  const c = useContext(InPlaceCtx);
  if (!c || (c.open?.fresh && c.open.at.i === i)) return null;
  return (
    <button type="button" className="ip-add" onClick={() => c.setOpen({ at: { i, j: count }, fresh: true })}>
      <Icon name="plus" />
      Add a line
    </button>
  );
}

/** The line that says the words can be changed, above them. */
export function InPlaceTip() {
  const native = useNative();
  return (
    <p className="ip-tip">
      <Icon name="pen" />
      {native ? "Tap any line to change it" : "Click any line to change it"}
    </p>
  );
}

function Editor({ as: T, at, initial, fresh = false }: { as: Tag; at: At; initial: string; fresh?: boolean }) {
  const c = useContext(InPlaceCtx);
  const native = useNative();
  const [v, setV] = useState(initial);
  const box = useRef<HTMLTextAreaElement>(null);
  const done = useRef(false);
  const line = typeof at.j === "number";
  useLayoutEffect(() => {
    const t = box.current;
    if (!t) return;
    t.focus();
    t.setSelectionRange(t.value.length, t.value.length);
  }, []);
  useLayoutEffect(() => {
    const t = box.current;
    if (!t) return;
    t.style.height = "auto";
    t.style.height = `${t.scrollHeight + 2}px`;
  }, [v]);
  if (!c) return null;
  const finish = (keep: boolean, next = false) => {
    if (done.current) return;
    done.current = true;
    if (keep) c.save(at, v, fresh, next);
    else c.setOpen(null);
  };
  return (
    <T
      className="ip-ed"
      onBlur={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget as Node | null)) finish(true);
      }}
    >
      <textarea
        ref={box}
        rows={1}
        aria-label={fresh ? "New line" : "Change this"}
        value={v}
        onChange={(e) => setV(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Escape") {
            e.preventDefault();
            finish(false);
          } else if (e.key === "Enter" && !e.shiftKey && at.j !== undefined) {
            e.preventDefault();
            finish(true, line);
          }
        }}
      />
      <span className="ip-edf">
        <span>{line && !native ? "Enter adds the next line." : ""}</span>
        <button type="button" className={native ? "pn-btn sm pri" : "btn sm"} onMouseDown={hold} onClick={() => finish(true)}>
          Done
        </button>
        <button type="button" className={native ? "pn-btn sm ghost" : "btn sm ghost"} onMouseDown={hold} onClick={() => finish(false)}>
          Cancel
        </button>
      </span>
    </T>
  );
}
